import { type UseQueryResult, useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { AWP_STALE_TIME, awpKeys, requestAwpPanel } from './awpApi';
import { buildAwpDashboard } from './model/buildAwpDashboard';
import type { AwpDashboard, AwpPanel } from './model/types';
import { useAwpVisits } from './useAwpVisits';

/**
 * - `loading`: the visits or the current visit's panel haven't arrived yet.
 * - `error`: either failed and there's nothing to show. `error` holds the failure; read its code with
 *   `extractGqlErrorCode`.
 * - `empty`: no AWP on record, or no member to ask about.
 * - `ready`: the current visit's panel is in `data`, whatever the previous visits' panels are doing.
 */
export type AwpDashboardStatus = 'loading' | 'error' | 'empty' | 'ready';

/** One previous visit's panel load state, aligned to `visitDates[i + 1]`. */
export type PreviousVisitStatus = 'loading' | 'ready' | 'failed';

export interface AwpDashboardResult {
  data: AwpDashboard | undefined;
  status: AwpDashboardStatus;
  /** What failed when `status` is `error` (the visits, else the current panel); null otherwise. */
  error: Error | null;
  /** `history[i]` is `visitDates[i + 1]`'s panel: shorter than `HISTORY_DEPTH` for a member with fewer visits. */
  history: PreviousVisitStatus[];
  /** Refetches the visits and the current panel; previous visits' panels aren't retried. Does nothing without a member id. */
  retry: () => void;
}

interface PanelQueryState {
  panels: AwpPanel[];
  current: { hasData: boolean; error: Error | null } | undefined;
  history: PreviousVisitStatus[];
}

/**
 * Only data, never the query results' functions, so TanStack's structural sharing keeps `panels` the same array
 * until a panel's data actually changes — a background refetch or a fetch-state flip doesn't rebuild the model.
 * Module scope keeps the function itself stable, which `useQueries` needs to reuse its last result.
 */
function combinePanels(results: UseQueryResult<AwpPanel>[]): PanelQueryState {
  const [current, ...previous] = results;
  return {
    panels: results.flatMap((result) => (result.data ? [result.data] : [])),
    current: current && { hasData: current.data !== undefined, error: current.error },
    history: previous.map((result) => (result.data !== undefined ? 'ready' : result.error ? 'failed' : 'loading')),
  };
}

/**
 * A member's AWP dashboard: their visits from portals-api, then one labs-api panel per visit (the current one and
 * `HISTORY_DEPTH` before it), built into the view model once the current one is in.
 *
 * Each visit is its own query, so loading earlier visits for trends is a matter of raising `HISTORY_DEPTH`. The
 * current visit's panel stays fresh for `AWP_STALE_TIME` like before; a previous visit's panel never goes stale —
 * once a finalized visit has loaded, it's never worth re-decrypting. Keys carry the member id, and no placeholder
 * data is kept, so switching members never shows the last member's labs while the next one's load.
 */
export function useAwpDashboard(memberId: string | undefined): AwpDashboardResult {
  const queryClient = useQueryClient();
  const visits = useAwpVisits(memberId);
  const currentVisit = visits.data?.[0];

  const { panels, current, history } = useQueries({
    queries: memberId
      ? (visits.data ?? []).map((date, index) => ({
          queryKey: awpKeys.panel(memberId, date),
          queryFn: ({ signal }: { signal: AbortSignal }) => requestAwpPanel(memberId, date, signal),
          staleTime: index === 0 ? AWP_STALE_TIME : Number.POSITIVE_INFINITY,
        }))
      : [],
    combine: combinePanels,
  });

  // Rebuilt only when a panel's data or the visit list changes. Gated on the *current* panel having
  // data — not just any panel — so a current visit that's still pending or has failed is never read
  // as ready from an older visit's panel that happened to load first.
  const data = useMemo(
    () => (current?.hasData ? buildAwpDashboard(panels, visits.data) : undefined),
    [panels, current?.hasData, visits.data],
  );

  const retry = useCallback(() => {
    if (!memberId) return;
    void queryClient.refetchQueries({ queryKey: awpKeys.visits(memberId), exact: true });
    if (currentVisit) void queryClient.refetchQueries({ queryKey: awpKeys.panel(memberId, currentVisit), exact: true });
  }, [queryClient, memberId, currentVisit]);

  return { data, ...statusOf(memberId, visits, current), history, retry };
}

/** In order: a failure or a wait on the visits decides first, then the current panel's. Data on hand outranks a failed refetch. */
function statusOf(
  memberId: string | undefined,
  visits: UseQueryResult<string[]>,
  current: PanelQueryState['current'],
): Pick<AwpDashboardResult, 'status' | 'error'> {
  if (!memberId) return { status: 'empty', error: null };
  if (visits.data === undefined) {
    return visits.error ? { status: 'error', error: visits.error } : { status: 'loading', error: null };
  }
  if (visits.data.length === 0) return { status: 'empty', error: null };
  if (!current?.hasData) {
    return current?.error ? { status: 'error', error: current.error } : { status: 'loading', error: null };
  }
  return { status: 'ready', error: null };
}
